import { Card } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { ChecklistResultStatsQueryDTO } from "@/modules/checklist/domain/contracts/result";
import { formatDateForInput } from "@/shared/lib/date-time";
import { useChecklistExecutionStats } from "../api/queries";
import { ChecklistResultByDepartment } from "../ui/ChecklistResultByDepartment";
import { ChecklistResultCharts } from "../ui/ChecklistResultCharts";
import { ChecklistResultFilter } from "../ui/ChecklistResultFilter";
import { ChecklistResultIncompleteItems } from "../ui/ChecklistResultIncompleteItems";
import { ChecklistResultRecentPartial } from "../ui/ChecklistResultRecentPartial";
import { ChecklistResultStatsCards } from "../ui/ChecklistResultStatsCards";
import { ChecklistResultTrend } from "../ui/ChecklistResultTrend";

export function ChecklistResultPage() {
  const nav = useNavigate();
  const searchParams = useSearch({ strict: false }) as Record<
    string,
    string | undefined
  >;

  const [departmentId, setDepartmentId] = useState<string>(
    searchParams.departmentId ?? "",
  );
  const [checklistId, setChecklistId] = useState<string>(
    searchParams.checklistId ?? "",
  );
  const [startDate, setStartDate] = useState<string>(
    searchParams.startDate ??
      formatDateForInput(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)),
  );
  const [endDate, setEndDate] = useState<string>(
    searchParams.endDate ?? formatDateForInput(new Date()),
  );

  const query: ChecklistResultStatsQueryDTO = useMemo(
    () => ({
      departmentId: departmentId || undefined,
      checklistId: checklistId || undefined,
      startDate: startDate ? `${startDate}T00:00:00.000Z` : undefined,
      endDate: endDate ? `${endDate}T23:59:59.999Z` : undefined,
    }),
    [departmentId, checklistId, startDate, endDate],
  );

  const { data: stats, isLoading } = useChecklistExecutionStats(query);

  const handleFilterChange = () => {
    nav({
      to: "/app/checklists/result",
      search: {
        departmentId: departmentId || undefined,
        checklistId: checklistId || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      },
    });
  };

  const handleExecutionClick = (executionId: string) => {
    nav({
      to: "/app/checklists/execution-history/$executionId",
      params: { executionId },
    });
  };

  return (
    <>
      <Header />
      <Main>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-bold text-2xl">ສະຫຼຸບຜົນ Checklist</h1>
              <p className="mt-1 text-muted-foreground">
                ສະຖິຕິການບັນທຶກ Checklist ຕາມຊ່ວງເວລາ ແລະ ພະແນກ
              </p>
            </div>
          </div>

          <ChecklistResultFilter
            departmentId={departmentId}
            checklistId={checklistId}
            startDate={startDate}
            endDate={endDate}
            onDepartmentChange={setDepartmentId}
            onChecklistChange={setChecklistId}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onFilterChange={handleFilterChange}
          />

          {isLoading ? (
            <div className="flex min-h-[400px] items-center justify-center">
              <div className="text-center">
                <div className="mx-auto h-8 w-8 animate-spin rounded-full border-primary border-b-2" />
                <p className="mt-2 text-muted-foreground">ກໍາລັງໂຫຼດ...</p>
              </div>
            </div>
          ) : stats ? (
            <>
              <ChecklistResultStatsCards stats={stats} />

              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <ChecklistResultCharts stats={stats} />
                <ChecklistResultByDepartment stats={stats} />
              </div>

              <ChecklistResultIncompleteItems stats={stats} />

              <ChecklistResultRecentPartial
                stats={stats}
                onExecutionClick={handleExecutionClick}
              />

              <ChecklistResultTrend stats={stats} />

              {stats.overall.total === 0 && (
                <Card className="p-12 text-center">
                  <p className="text-lg text-muted-foreground">
                    ບໍ່ມີການບັນທຶກ Checklist ໃນຊ່ວງເວລາທີ່ເລືອກ
                  </p>
                </Card>
              )}
            </>
          ) : null}
        </div>
      </Main>
    </>
  );
}
